'use client';

import { useEffect, useState } from 'react';

import { useTheme } from 'next-themes';
import { flushSync } from 'react-dom';

/**
 * The live `dark` state, read off the class rather than `resolvedTheme`.
 *
 * The provider sets `enableSystem={false}`, so next-themes resolves the theme in its state
 * initialiser and never re-renders — anything rendered from `resolvedTheme` keeps the server's
 * light answer. The class is what the CSS already believes, so this is the same source the visual
 * uses, and a `MutationObserver` catches the flips that happen inside a view transition.
 */
function useIsDark() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    function sync() {
      setIsDark(root.classList.contains('dark'));
    }
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

export function useThemeToggle() {
  const { setTheme, resolvedTheme } = useTheme();
  const isDark = useIsDark();

  function toggleTheme() {
    const nextTheme = resolvedTheme === 'dark' ? 'light' : 'dark';

    if (
      !document.startViewTransition ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setTheme(nextTheme);
      return;
    }

    document.startViewTransition(() => {
      flushSync(() => {
        setTheme(nextTheme);
      });
    });
  }

  return { toggleTheme, isDark };
}
